<template>
  <div class="w-full bg-blue-40">
    <div
      class="flex justify-center bg-blue-50 py-3 text-2xl font-bold text-white lg:justify-start lg:px-4"
    >
      <icon-search class="mr-4 h-6 w-6" />
      <template v-if="this.pageType === 'search'">
        <span class="block">Search 9,000+ agencies</span>
        <span class="hidden font-normal sm:block"
          >&nbsp;- Find the best match for you</span
        >
      </template>
      <template v-else>
        <span class="block md:hidden">Agency Search</span>
        <span class="hidden md:block"
          >Search UK Agencies to help you find the perfect job or hire the right
          staff</span
        >
      </template>
    </div>
    <form
      class="flex flex-col gap-x-6 gap-y-2 px-4 py-4 md:grid md:grid-cols-2 lg:grid-cols-3"
    >
      <div>
        <label class="pb-2 text-base leading-5 text-white">
          Industry (required)
        </label>
        <industry-select
          class="w-full"
          placeholder="Please select an industry"
          :value="industry"
          :error="this.errors.industry"
          errorMessage="You must select an industry from the list"
          @onClick="onIndustrySelectClick"
        />
      </div>
      <div>
        <label class="pb-2 text-base leading-5 text-white"
          >Industry Sub Sector</label
        >
        <input-dropdown
          class="w-full"
          placeholder="Select a sub sector"
          :items="skillOptions"
          :value="skill"
          :key="inputKey('skill', skill)"
          @change="(value) => changeParameter('skill', value)"
        ></input-dropdown>
      </div>
      <div>
        <label class="pb-2 text-base leading-5 text-white"
          >You are... (required)</label
        >
        <input-dropdown
          class="w-full"
          placeholder="Please select"
          :items="userTypes"
          :value="emp_cand"
          :error="this.errors.emp_cand"
          :key="inputKey('emp_cand', emp_cand)"
          errorMessage="You must select an option from the list"
          @change="(value) => changeParameter('emp_cand', value)"
        ></input-dropdown>
      </div>
      <div>
        <label class="pb-2 text-base leading-5 text-white">Location</label>
        <InputLocation
          class="w-full"
          :value="currentLocationOption"
          :locations="locationOptions"
          :error="this.errors.location"
          errorMessage="You must choose a location"
          @changeLocation="(value) => updateLocation(value)"
        ></InputLocation>
      </div>
      <div>
        <label class="pb-2 text-base leading-5 text-white"
          >Employment Type</label
        >
        <input-dropdown
          class="w-full"
          placeholder="Employment Type"
          :items="employmentTypes"
          :value="job_type"
          :key="inputKey('job_type', job_type)"
          @change="(value) => changeParameter('job_types', [value])"
        ></input-dropdown>
      </div>
      <div class="mt-6">
        <input-button
          :disabled="fetching"
          block
          styleType="search"
          @onClick="onSearch"
        >
          <template v-if="!fetching">Search 9,000+ Agencies</template>
          <template v-else
            >Loading
            <icon-arrow-circle
              class="inline-block h-4 w-4 animate-spin stroke-white"
            />
          </template>
        </input-button>
      </div>
    </form>
    <industry-select-modal
      :visible="showIndustrySelect"
      @onBackgroundClick="onIndustrySelectBackgroundClick"
      @onContentCloseClick="onIndustrySelectClose"
      @selectIndustry="onIndustrySelect"
    />
  </div>
</template>

<script>
import InputButton from 'Components/InputButton.vue';
import AcCard from 'Components/AcCard.vue';
import InputDropdown from 'Components/InputDropdown.vue';
import IconArrowCircle from 'Components/Icons/IconArrowCircle';
import IconSearch from 'Components/Icons/IconSearch';
import IndustrySelect from 'Components/IndustrySelect';
import IndustrySelectModal from 'Components/Modals/IndustrySelectModal';
import validates, { validators } from 'Mixins/validates';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import uuid from 'Mixins/uuid';
import locations from 'Helpers/locations.json';
import InputLocation from 'Components/InputLocation.vue';

export default {
  name: 'SearchBar',
  components: {
    InputLocation,
    IconArrowCircle,
    InputButton,
    AcCard,
    InputDropdown,
    IconSearch,
    IndustrySelect,
    IndustrySelectModal,
  },
  mixins: [validates, uuid],
  props: {
    pageType: {
      type: String,
      default: 'home',
      validator(value) {
        return ['search', 'home', 'article'].includes(value);
      },
    },
    industry: {
      type: String,
      default: null,
    },
    skill: {
      type: String,
      default: null,
    },
    location: {
      type: Object,
      default: null,
    },
    job_type: {
      type: String,
      default: null,
    },
    emp_cand: {
      type: String,
      default: null,
    },
    skillOptions: {
      type: Object,
      default: () => ({}),
    },
    employmentTypes: {
      type: Object,
      default: () => ({
        any: 'Any',
        contract: 'Contract',
        permanent: 'Permanent',
        temptoperm: 'Temporary to Permanent',
        temporary: 'Temporary',
        parttimejobshare: 'Part Time Position',
      }),
    },
    userTypes: {
      type: Object,
      default: () => ({
        cnd: "I'm a candidate, looking for a job",
        emp: "I'm an employer, looking for staff",
        neither: 'Something else',
      }),
    },
  },
  data() {
    return {
      showIndustrySelect: false,
      locationOptions: locations,
    };
  },
  computed: {
    ...mapState('search', ['fetching']),
    ...mapGetters('search', ['currentLocationOption', 'searchQuery']),
    ...mapGetters('pagetext', ['getLocationSyskey']),
    syskeyValue() {
      return this.getLocationSyskey;
    },
  },
  methods: {
    ...mapActions('industry', ['getIndustrySkills']),
    ...mapActions('search', ['doSearch']),
    ...mapActions('pagetext', ['fetchLocationSyskey']),
    ...mapMutations('search', ['setParameter']),
    ...mapMutations('contact', { setRequestorType: 'setType' }),
    async onSearch() {
      const syskeyValue = this.syskeyValue;

      this.resetErrors();
      // Allow the dom to render the no error state before validating again so error messages
      // properly popup
      await this.$nextTick();

      const validationRules = {
        industry: [validators.required],
        emp_cand: [validators.required],
      };

      if (syskeyValue.key_value == 1) {
        validationRules.location = [validators.required];
      }

      if (
          !this.validate(
              validationRules,
              {
                industry: this.industry,
                emp_cand: this.emp_cand,
                location: this.location,
              }
          )
      ) {
        return;
      }
      this.changeParameter('page', 1);

      jQuery(document).trigger('tc_search');

      // always ensure the cookie syncs to the form on submit
      this.setRequestorType(this.emp_cand);

      this.link_to({
        name: 'search',
        query: this.searchQuery,
      }, true);
    },
    onIndustrySelectClick() {
      this.showIndustrySelect = true;
    },
    onIndustrySelectBackgroundClick() {
      this.showIndustrySelect = false;
    },
    onIndustrySelectClose() {
      this.showIndustrySelect = false;
    },
    async onIndustrySelect(industry) {
      this.showIndustrySelect = false;
      // If the same industry is selected again, do nothing.
      if (industry === this.industry) {
        return;
      }
      // Otherwise, change the industry, reset subcat choice, and load a new list
      // of subcats in the search bar
      this.changeParameter('industry', industry);
      this.changeParameter('skill', null);
      this.getIndustrySkills(industry);
    },
    changeParameter(key, value) {
      delete this.errors[key];
      this.setParameter({ key, value });
    },
    updateLocation(loc) {
        let locID = '', locName = '';
        if (loc == null) {
        } else {
            locID = loc.id;
            locName = loc.location;
        }
        this.changeParameter('location_id', locID);
        this.changeParameter('location', locName)
    },
    inputKey(input, value) {
      return `${this.uuid}-${input}-${value}`;
    },
  },
  async mounted() {
    await this.fetchLocationSyskey();
  },
};
</script>
